import { SseFrameBuffer } from './sse-frame-buffer';

export type ReadSseStreamOptions = {
  /** Resolved stream URL (from a `kind: 'stream'` endpoint on the typed client). */
  url: string;
  /** Current in-memory access token, or null when signed out. */
  token: string | null;
  /** Aborts the in-flight fetch + reader (wire to the caller's unmount). */
  signal?: AbortSignal;
  /** Invoked once the response head is accepted, before the first frame is read. */
  onOpen?: () => void;
  /** Invoked for every completed frame's `data:` payload, in order. */
  onPayload: (payload: string) => void;
  /** Called once on a 401 to obtain a fresh token; return null to give up. */
  refresh?: () => Promise<string | null>;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
};

function buildInit(token: string | null, signal: AbortSignal | undefined): RequestInit {
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'X-Immo-Client': 'web',
  };

  if (token !== null) headers['Authorization'] = `Bearer ${token}`;

  return { method: 'GET', headers, credentials: 'include', ...(signal ? { signal } : {}) };
}

/**
 * Consumes an SSE stream with an authenticated `fetch` reader. We cannot use
 * `EventSource` because the API authenticates workspace routes with an in-memory
 * Bearer token and `EventSource` cannot set the `Authorization` header. On a 401
 * the token is refreshed once and the stream reconnected. Resolves when the
 * stream closes for any reason (server close, transport drop, abort) — the caller
 * decides whether that warrants a reconnect.
 */
export async function readSseStream(options: ReadSseStreamOptions): Promise<void> {
  const fetchImpl = options.fetchImpl ?? fetch;

  let response = await fetchImpl(options.url, buildInit(options.token, options.signal));

  if (response.status === 401 && options.refresh) {
    const refreshed = await options.refresh();

    if (refreshed !== null) {
      response = await fetchImpl(options.url, buildInit(refreshed, options.signal));
    }
  }

  if (!response.ok || response.body === null) return;

  options.onOpen?.();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const frames = new SseFrameBuffer();

  try {
    for (;;) {
      const { done, value } = await reader.read();

      if (done) break;

      for (const payload of frames.push(decoder.decode(value, { stream: true }))) {
        options.onPayload(payload);
      }
    }
  } catch {
    // Aborted or transport dropped — never fatal for a best-effort stream.
  } finally {
    reader.releaseLock();
  }
}

export function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
